<script setup>
import { railsApi } from '@/utils/railsApi'
import { formatCurrency } from '@core/utils/formatters'
import StatusChip from '@/components/shared/StatusChip.vue'

definePage({
  meta: {
    adminArea: true,
  },
})

const { t } = useI18n()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const invoices = ref([])
const q = ref('')
const trainerId = ref('')
const lifecycleState = ref('')
const issuedFrom = ref('')
const issuedTo = ref('')

const lifecycleOptions = computed(() => [
  { title: t('admin.invoices.lifecycleAny'), value: '' },
  { title: 'Concept', value: 'draft' },
  { title: 'Verzonden', value: 'sent' },
  { title: 'Betaald', value: 'paid' },
  { title: 'Achterstallig', value: 'overdue' },
  { title: 'Geannuleerd', value: 'void' },
])

const formatDate = value => {
  if (!value)
    return '—'

  return new Date(value).toLocaleDateString('nl-NL')
}

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const params = new URLSearchParams()
    if (q.value.trim())
      params.set('q', q.value.trim())
    if (trainerId.value.trim())
      params.set('trainer_id', trainerId.value.trim())
    if (lifecycleState.value)
      params.set('lifecycle_state', lifecycleState.value)
    if (issuedFrom.value)
      params.set('issued_from', issuedFrom.value)
    if (issuedTo.value)
      params.set('issued_to', issuedTo.value)

    const query = params.toString()
    const data = await railsApi(`/api/v1/admin/platform_invoices${query ? `?${query}` : ''}`)

    invoices.value = data.platform_invoices || []
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.invoices.loadError')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

const onSearch = () => {
  load()
}

const openInvoice = id => {
  router.push({ name: 'admin-invoices-id', params: { id } })
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-4">
      <h1 class="text-h4 mb-0">
        {{ $t('admin.invoices.title') }}
      </h1>
    </div>

    <VCard
      class="mb-4"
      flat
      border
    >
      <VCardText>
        <VForm @submit.prevent="onSearch">
          <VRow>
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="q"
                :label="$t('admin.invoices.search')"
                :placeholder="$t('admin.invoices.searchPlaceholder')"
                clearable
              />
            </VCol>
            <VCol
              cols="12"
              md="2"
            >
              <VTextField
                v-model="trainerId"
                :label="$t('admin.invoices.trainerId')"
                clearable
              />
            </VCol>
            <VCol
              cols="12"
              md="2"
            >
              <VSelect
                v-model="lifecycleState"
                :items="lifecycleOptions"
                item-title="title"
                item-value="value"
                :label="$t('admin.invoices.lifecycle')"
              />
            </VCol>
            <VCol
              cols="12"
              md="2"
            >
              <VTextField
                v-model="issuedFrom"
                type="date"
                :label="$t('admin.invoices.issuedFrom')"
              />
            </VCol>
            <VCol
              cols="12"
              md="2"
            >
              <VTextField
                v-model="issuedTo"
                type="date"
                :label="$t('admin.invoices.issuedTo')"
              />
            </VCol>
            <VCol
              cols="12"
              class="d-flex justify-end"
            >
              <VBtn
                type="submit"
                color="primary"
                :loading="loading"
              >
                {{ $t('admin.invoices.search') }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>

    <VCard
      flat
      border
    >
      <VCardText>
        <div
          v-if="loading"
          role="status"
        >
          {{ $t('common.loading') }}
        </div>

        <VAlert
          v-else-if="!invoices.length"
          type="info"
          variant="tonal"
          role="status"
        >
          {{ $t('admin.invoices.empty') }}
        </VAlert>

        <VTable
          v-else
          density="comfortable"
        >
          <thead>
            <tr>
              <th scope="col">
                {{ $t('admin.invoices.columns.number') }}
              </th>
              <th scope="col">
                {{ $t('admin.invoices.columns.trainer') }}
              </th>
              <th scope="col">
                {{ $t('admin.invoices.columns.amount') }}
              </th>
              <th scope="col">
                {{ $t('admin.invoices.columns.issued') }}
              </th>
              <th scope="col">
                {{ $t('admin.invoices.columns.due') }}
              </th>
              <th scope="col">
                {{ $t('admin.invoices.columns.status') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invoice in invoices"
              :key="invoice.id"
              style="cursor: pointer;"
              tabindex="0"
              @click="openInvoice(invoice.id)"
              @keydown.enter="openInvoice(invoice.id)"
            >
              <td>{{ invoice.invoice_number }}</td>
              <td>
                {{ invoice.trainer?.business_name || invoice.trainer?.name || invoice.trainer?.email || invoice.trainer_id }}
              </td>
              <td>{{ formatCurrency(invoice.amount_cents, invoice.currency) }}</td>
              <td>{{ formatDate(invoice.issued_on) }}</td>
              <td>{{ formatDate(invoice.due_on) }}</td>
              <td>
                <StatusChip
                  domain="platform_invoice"
                  :state="invoice.lifecycle_state"
                />
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>
    </VCard>
  </div>
</template>
